<script lang="ts">
import { BadgeErrorSm, BadgeSuccessSm, BadgeWarningSm } from "@easy/ui/badges";
import { CustomDomain } from "@/apps/page/project/sidebar/settings/custom-domain.interface";
import { Component, markRaw } from "vue";

const BADGE_COMPONENT: Record<CustomDomain["status"], Component> = {
    pending: markRaw(BadgeWarningSm),
    configured: markRaw(BadgeSuccessSm),
    failed: markRaw(BadgeErrorSm),
};
</script>

<script setup lang="ts">
import { NotificationAlert } from "@easy/ui/notifications";
import { ButtonSecondarySm } from "@easy/ui/buttons";
import { CardDropdownSm } from "@easy/ui/cards";
import { AddIcon, LinkChainIcon, TrashBinIcon } from "@easy/ui/icons";
import { computed, ref } from "vue";
import { t } from "@/i18n";
import { ModalInput, ModalDanger } from "@easy/ui/modals";
import { useAsyncState } from "@vueuse/core";
import { customDomainsService } from "@/apps/page/project/sidebar/settings/custom-domains.service";
import { PageProject } from "@/apps/page/page-project.interface";
import { FormInputUrl } from "@easy/ui/forms";

const props = defineProps<{ pageUuid: PageProject["uuid"] }>();

const {
    state: domainList,
    execute: reloadDomainList,
    isLoading,
} = useAsyncState<CustomDomain[]>(() => customDomainsService.getAll(props.pageUuid), [], {
    immediate: true,
});

const baseDomain = customDomainsService.getBaseDomain();
const showAddDomainModal = ref(false);
const domainToDelete = ref("");
const showDeleteModal = computed(() => Boolean(domainToDelete.value));
const newDomainName = ref("");
const newDomainError = ref("");

// this code assumes all domains having same IP address info !
const pendingDomainIpInfo = computed(
    () =>
        domainList.value
            .filter((d) => d.status === "pending")
            .map((d) => d.ipAddress)
            .at(0) || ""
);

const domainNames = computed(() => domainList.value.map((d) => d.domain || ""));

function makeBadge(status: CustomDomain["status"]) {
    return {
        component: BADGE_COMPONENT[status],
        label: t(`page.settings.custom_domains.statuses.${status}`),
    };
}

async function addNewDomain() {
    if (!newDomainName.value.trim() || isLoading.value) {
        return;
    }
    const newDomain = newDomainName.value.toLowerCase().trim().replace("https://", "");
    if (domainNames.value.includes(newDomain)) {
        newDomainError.value = t("page.settings.custom_domains.error.already_taken");
        return;
    }
    isLoading.value = true;
    newDomainError.value = "";
    try {
        await customDomainsService.add(props.pageUuid, newDomain);
        showAddDomainModal.value = false;
        newDomainName.value = "";
        await reloadDomainList();
    } catch (e) {
        // @ts-expect-error
        newDomainError.value = e?.translatedMessage;
        newDomainName.value = newDomain;
    } finally {
        isLoading.value = false;
    }
    return;
}

async function deleteDomain() {
    isLoading.value = true;
    await customDomainsService.remove(domainToDelete.value);
    isLoading.value = false;
    domainToDelete.value = "";
    await reloadDomainList();
}

const newDomainModalData = computed(() => ({
    title: t("page.settings.custom_domains.domain_name"),
    ctaLabel: t("shared.add"),
    cancelLabel: t("shared.cancel"),
    ctaLoading: isLoading.value,
}));

const deleteModalData = computed(() => ({
    title: t("page.settings.custom_domains.delete_modal.title"),
    ctaLabel: t("shared.delete"),
    cancelLabel: t("shared.cancel"),
    ctaLoading: isLoading.value,
}));

function makeDomainActions(domain: string) {
    return {
        title: domain,
        actions: [
            {
                label: t("shared.delete"),
                icon: TrashBinIcon,
                isDanger: true,
                handler: () => (domainToDelete.value = domain),
            },
        ],
    };
}
</script>

<template>
    <NotificationAlert class="my-3" v-if="pendingDomainIpInfo" :close-button="false">
        <p v-html="$t('page.settings.custom_domains.dns_info', { ip: pendingDomainIpInfo })" />
    </NotificationAlert>
    <div class="mb-2" v-for="{ id, domain, status } of domainList" :key="id">
        <CardDropdownSm :item="makeDomainActions(domain)">
            <template #icon>
                <LinkChainIcon class="flex-shrink-0 w-4 h-4 ct-fg-2" />
            </template>
            <template #extra>
                <component :is="makeBadge(status).component" class="mr-2">
                    {{ makeBadge(status).label }}
                </component>
            </template>
        </CardDropdownSm>
    </div>
    <div class="mt-6">
        <ButtonSecondarySm class="w-full" @click="showAddDomainModal = true">
            <AddIcon />
            <span>{{ $t("page.settings.custom_domains.add") }}</span>
        </ButtonSecondarySm>
    </div>
    <ModalInput
        v-model:open="showAddDomainModal"
        :modal-data="newDomainModalData"
        @cta="addNewDomain"
        @cancel="showAddDomainModal = false"
    >
        <template #icon>
            <LinkChainIcon />
        </template>
        <template #fields>
            <FormInputUrl
                autofocus
                v-model="newDomainName"
                @keydown.enter="addNewDomain"
                :title="$t('page.settings.custom_domains.domain_name_title')"
                :help="$t('page.settings.custom_domains.domain_name_help')"
                :placeholder="$t('page.settings.custom_domains.domain_placeholder')"
                :error="newDomainError"
            />
        </template>
    </ModalInput>
    <ModalDanger
        v-if="showDeleteModal"
        v-model="showDeleteModal"
        :modal-data="deleteModalData"
        @cta="deleteDomain"
        @cancel="domainToDelete = ''"
    >
        <template #icon>
            <TrashBinIcon />
        </template>

        <p
            v-html="
                $t('page.settings.custom_domains.delete_modal.description', {
                    domain: baseDomain,
                })
            "
        />
    </ModalDanger>
</template>
